(() => {
  'use strict';
  // Local demo only: no network, cookies, analytics, cart or persistent storage.
  const TAG = 'fotografista-personal-print';
  if (customElements.get(TAG)) return;
  const MAX_BYTES = 10 * 1024 * 1024;
  const MAX_PIXELS = 40_000_000;
  function dimensions(bytes) {
    const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.length >= 33 && [137,80,78,71,13,10,26,10].every((b,i) => bytes[i] === b) && v.getUint32(8) === 13 && v.getUint32(12) === 0x49484452) {
      return {width:v.getUint32(16), height:v.getUint32(20), mime:'image/png'};
    }
    if (bytes[0] !== 255 || bytes[1] !== 216) throw Error('fileInvalid');
    let p = 2;
    while (p + 4 <= bytes.length) {
      if (bytes[p++] !== 255) throw Error('fileInvalid');
      while (bytes[p] === 255) p++;
      const marker = bytes[p++];
      if (marker === 0xD9 || marker === 0xDA) break;
      if (marker === 0x01 || (marker >= 0xD0 && marker <= 0xD7)) continue;
      if (p + 2 > bytes.length) break;
      const length = v.getUint16(p);
      if (length < 2 || p + length > bytes.length) throw Error('fileInvalid');
      if ([0xC0,0xC1,0xC2].includes(marker) && length >= 8) return {width:v.getUint16(p+5),height:v.getUint16(p+3),mime:'image/jpeg'};
      p += length;
    }
    throw Error('fileInvalid');
  }
  class PersonalPrint extends HTMLElement {
    // Frame Studio v3.12 presets and fit calculation, in millimetres.
    // Match its nearest ratio group, orientation, mat, visible paper border,
    // and 1000 mm glass-size threshold. Never crop the uploaded photograph.
    static previewGeometry(tier, width, height, framed, withMat, settings = {}) {
      if (![width, height].every(v => Number.isFinite(v) && v > 0)) return null;
      const index = ['small','medium','large'].indexOf(tier);
      if (index < 0) return null;
      const ratios = [1,1.25,1.5];
      const ratio = Math.max(width,height) / Math.min(width,height);
      const group = ratios.reduce((best,value,i) =>
        Math.abs(value-ratio) < Math.abs(ratios[best]-ratio) ? i : best, 0);
      const presets = [[[500,500],[700,700],[1000,1000]],
        [[500,430],[700,590],[1000,830]], [[500,380],[700,500],[1000,700]]];
      const [long,short] = presets[group][index];
      const glassW = height > width ? short : long, glassH = height > width ? long : short;
      const defaults = {matSmall:55, matLarge:60, frameSmall:20, frameLarge:30, border:10};
      const config = Object.fromEntries(Object.entries(defaults).map(([key, fallback]) =>
        [key, Number.isFinite(settings[key]) && settings[key] >= 0 ? settings[key] : fallback]));
      const baseMat = withMat ? config[long >= 1000 ? 'matLarge' : 'matSmall'] : 0;
      const profile = config[long >= 1000 ? 'frameLarge' : 'frameSmall'];
      const availableW = glassW - 2*(baseMat+config.border);
      const availableH = glassH - 2*(baseMat+config.border);
      if (availableW <= 0 || availableH <= 0) return null;
      const fit = Math.min(availableW/width, availableH/height);
      const photoW = width*fit, photoH = height*fit;
      const border = framed ? config.border : 0, frame = framed ? profile : 0;
      const paperW = photoW+2*border, paperH = photoH+2*border;
      const matX = framed ? (glassW-paperW)/2 : 0;
      const matY = framed ? (glassH-paperH)/2 : 0;
      return {photoW,photoH,paperW,paperH,border,frame,matX,matY,group:ratios[group],
        outerW:framed ? glassW+2*frame : photoW, outerH:framed ? glassH+2*frame : photoH};
    }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.form = this.querySelector('form');
      if (!this.form || this.dataset.mode !== 'demo') return;
      this.generation ??= 0;
      this.previewSettings = JSON.parse(this.querySelector('[data-preview-settings]')?.textContent || '{}');
      const signal = this.controller.signal;
      this.form.addEventListener('submit', e => { e.preventDefault(); this.submit(); }, {signal});
      this.querySelector('[data-submit]').addEventListener('click', () => this.submit(), {signal});
      this.form.addEventListener('change', e => {
        this.clearResult();
        this.updateChoices();
        if (e.target.matches('[data-file]')) this.readFile();
        if (e.target.name === 'alternate_transfer') {
          if (e.target.checked) this.clearFile();
          this.querySelector('[data-file]').disabled = e.target.checked;
        }
      }, {signal});
      this.form.addEventListener('input', () => this.clearResult(), {signal});
      this.resizeObserver = new ResizeObserver(() => this.updatePreview());
      this.resizeObserver.observe(this.querySelector('[data-preview]'));
      this.querySelector('[data-controls]').disabled = false;
      this.updateChoices();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      if (this.form) this.clearFile();
    }
    field(name) { return this.form.elements.namedItem(name); }
    clearResult() {
      this.querySelector('[data-result]').hidden = true;
      const error = this.querySelector('[data-error]');
      error.hidden = true;
      this.form.querySelectorAll('[aria-invalid]').forEach(e => e.removeAttribute('aria-invalid'));
    }
    error(key, field) {
      const el = this.querySelector('[data-error]');
      el.textContent = this.messages[key] || this.messages.fileInvalid;
      el.hidden = false;
      if (field) { field.setAttribute('aria-invalid','true'); field.setAttribute('aria-describedby',el.id); field.focus(); }
      else el.focus();
    }
    updateChoices() {
      const framed = this.field('finish')?.value === 'framed';
      for (const name of ['frame','glass']) {
        const field = this.field(name);
        if (field) { field.disabled = !framed; field.closest('[data-group]').hidden = !framed; }
      }
      const custom = this.field('size')?.value === 'custom';
      this.querySelector('[data-custom-size]').hidden = !custom;
      this.field('custom_size').disabled = !custom;
      this.querySelector('[data-price]').textContent = this.messages[custom ? 'customPrice' : 'standardPrice'];
      this.updateSizeOptions();
      this.updateResolution();
      this.updatePreview();
    }
    clearFile(resetInput = true) {
      this.generation++;
      this.fileInfo = null;
      this.previewImage = null;
      this.pending = false;
      if (resetInput) this.querySelector('[data-file]').value = '';
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
      const image = this.querySelector('[data-image]');
      image.hidden = true;
      image.removeAttribute('src');
      this.querySelector('[data-placeholder]').hidden = false;
      this.querySelector('[data-file-status]').textContent = '';
      this.updateSizeOptions();
      this.updatePreview();
    }
    async readFile() {
      const input = this.querySelector('[data-file]');
      const file = input.files?.[0];
      this.clearFile(false);
      if (!file) return;
      const generation = this.generation;
      this.pending = true;
      this.querySelector('[data-file-status]').textContent = this.messages.filePending;
      let url;
      try {
        if (file.size > MAX_BYTES) throw Error('fileLarge');
        if (file.size === 0 || !['image/png','image/jpeg'].includes(file.type)) throw Error('fileInvalid');
        const bytes = new Uint8Array(await file.arrayBuffer());
        const meta = dimensions(bytes);
        if (meta.mime !== file.type || !meta.width || !meta.height || meta.width * meta.height > MAX_PIXELS) throw Error('fileInvalid');
        url = URL.createObjectURL(file);
        const decoded = new Image();
        decoded.src = url;
        await decoded.decode();
        if (!decoded.naturalWidth || !decoded.naturalHeight) throw Error('fileInvalid');
        if (generation !== this.generation || !this.isConnected) { URL.revokeObjectURL(url); return; }
        this.objectUrl = url;
        this.previewImage = decoded;
        this.fileInfo = {name:file.name, size:file.size, width:decoded.naturalWidth, height:decoded.naturalHeight};
        const image = this.querySelector('[data-image]');
        image.src = url;
        image.width = decoded.naturalWidth;
        image.height = decoded.naturalHeight;
        image.hidden = false;
        this.querySelector('[data-placeholder]').hidden = true;
        this.updateSizeOptions();
        this.updateResolution();
        this.updatePreview();
      } catch (error) {
        if (url) URL.revokeObjectURL(url);
        if (generation !== this.generation || !this.isConnected) return;
        input.value = '';
        this.querySelector('[data-file-status]').textContent = '';
        this.error(error.message, input);
      } finally {
        if (generation === this.generation) this.pending = false;
      }
    }
    updateResolution() {
      if (!this.fileInfo) return;
      const f = this.fileInfo;
      const g = this.currentGeometry();
      const ppi = g ? Math.round(f.width * 25.4 / g.photoW) : null;
      const detail = [`${f.name} · ${f.width} × ${f.height} px`];
      if (ppi) detail.push(`~${ppi} ppi`);
      if (ppi && ppi < 150) detail.push(this.messages.lowResolution);
      this.querySelector('[data-file-status]').textContent = detail.join(' · ');
    }
    currentGeometry(tier = this.field('size')?.value, framed = this.field('finish')?.value === 'framed') {
      if (!this.fileInfo) return null;
      return PersonalPrint.previewGeometry(tier,this.fileInfo.width,this.fileInfo.height,
        framed,true,this.previewSettings);
    }
    formatCm(mm) { return (mm/10).toLocaleString('lt-LT',{maximumFractionDigits:1}); }
    updateSizeOptions() {
      const labels={small:'Mažas',medium:'Vidutinis',large:'Didelis'};
      const framed=this.field('finish')?.value === 'framed';
      for (const option of this.field('size').options) {
        if (!labels[option.value]) continue;
        const g=this.currentGeometry(option.value,framed);
        const dimensions=g ? `${this.formatCm(g.outerW)} × ${this.formatCm(g.outerH)} cm` : '';
        option.textContent=g ? `${labels[option.value]} — ${dimensions}${framed ? ' su rėmu' : ' vaizdas'}` : labels[option.value];
      }
    }
    updatePreview() {
      const canvas = this.querySelector('[data-canvas]');
      const caption = this.querySelector('[data-preview-dimensions]');
      if (!canvas || !caption) return;
      this.geometry = null;
      canvas.hidden = true;
      caption.textContent = '';
      const fallback = this.querySelector('[data-image]');
      if (!this.fileInfo || !this.previewImage) return;
      const framed = this.field('finish')?.value === 'framed';
      const g = this.currentGeometry();
      if (!g) {
        fallback.hidden = false;
        caption.textContent = 'Individualaus dydžio matmenis reikia suderinti. Kol kas rodoma originali fotografija be rėmo maketo.';
        return;
      }
      this.geometry = g;
      const cm = mm => (mm / 10).toLocaleString('lt-LT', {maximumFractionDigits:1});
      const size = (w,h) => `${cm(w)} × ${cm(h)} cm`;
      const parts = [`Vaizdas: ${size(g.photoW,g.photoH)}`];
      if (framed) parts.push(`Išorė su rėmu: ${size(g.outerW,g.outerH)}`,
        `Paspartas šonuose: ${cm(g.matX)} cm; viršuje / apačioje: ${cm(g.matY)} cm · Rėmelis: ${cm(g.frame)} cm · Baltas kraštelis: ${cm(g.border)} cm`);
      else parts.push('Tik atspaudas — be pasparto ir rėmelio.');
      if (framed && !['black','wood'].includes(this.field('frame')?.value)) parts.push('Rodomas juodo rėmo pavyzdys.');
      caption.textContent = parts.join(' · ');
      canvas.setAttribute('aria-label', `Fotografijos vizualizacija. ${caption.textContent}`);
      const ctx = canvas.getContext('2d');
      if (!ctx) { fallback.hidden = false; return; }
      fallback.hidden = true;
      canvas.hidden = false;
      const stage = this.querySelector('[data-preview]');
      const width = stage.clientWidth, height = stage.clientHeight;
      if (width <= 0 || height <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr,0,0,dpr,0,0);
      ctx.clearRect(0,0,width,height);
      const scale = Math.min((width - 48) / g.outerW, (height - 48) / g.outerH);
      const w = g.outerW * scale, h = g.outerH * scale;
      const x = (width-w)/2, y = (height-h)/2;
      const frame = g.frame * scale, matX = g.matX * scale, matY = g.matY * scale, border = g.border * scale;
      const wood = framed && this.field('frame')?.value === 'wood';
      ctx.save();
      ctx.shadowColor = '#00000038'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 6;
      ctx.fillStyle = framed ? (wood ? '#aa7a49' : '#1e1e1e') : '#fafafa';
      ctx.fillRect(x,y,w,h);
      ctx.restore();
      if (framed) {
        const gradient = ctx.createLinearGradient(x,y,x+w,y+h);
        gradient.addColorStop(0,wood ? '#cba16d' : '#353535');
        gradient.addColorStop(0.5,wood ? '#af804f' : '#141414');
        gradient.addColorStop(1,wood ? '#d5af7e' : '#252525');
        ctx.fillStyle = gradient; ctx.fillRect(x,y,w,h);
        // Miter joints at each corner, kept inside the physical frame profile.
        ctx.strokeStyle = wood ? '#65472755' : '#00000077'; ctx.lineWidth = 0.7;
        for (const [cx,cy,dx,dy] of [[x,y,1,1],[x+w,y,-1,1],[x+w,y+h,-1,-1],[x,y+h,1,-1]]) {
          ctx.beginPath(); ctx.moveTo(cx,cy); ctx.lineTo(cx+dx*frame,cy+dy*frame); ctx.stroke();
        }
      }
      const mx=x+frame, my=y+frame, mw=w-2*frame, mh=h-2*frame;
      ctx.fillStyle = '#fcfbf7'; ctx.fillRect(mx,my,mw,mh);
      if (matX > 0 || matY > 0) {
        // A subtle deterministic paper texture, adapted from Frame Studio.
        if (!this.matPattern) {
          const tile=document.createElement('canvas'); tile.width=64; tile.height=64;
          const tc=tile.getContext('2d'); tc.fillStyle='#fcfbf7'; tc.fillRect(0,0,64,64);
          tc.fillStyle='#665d490a';
          let seed=73;
          const random=() => { seed=(Math.imul(seed,1664525)+1013904223)>>>0; return seed/4294967296; };
          for (let i=0;i<1024;i++) tc.fillRect(Math.floor(random()*64),Math.floor(random()*64),1,1);
          this.matPattern=ctx.createPattern(tile,'repeat');
        }
        ctx.fillStyle=this.matPattern; ctx.fillRect(mx,my,mw,mh);
      }
      const px=mx+matX, py=my+matY, pw=g.paperW*scale, ph=g.paperH*scale;
      ctx.fillStyle='#fafafa'; ctx.fillRect(px,py,pw,ph);
      // Inner shadows preserve the measured mat and paper areas.
      const insetShadow=(sx,sy,sw,sh,depth,alpha) => {
        if (depth<=0) return;
        const top=ctx.createLinearGradient(0,sy,0,sy+depth);
        top.addColorStop(0,`rgba(0,0,0,${alpha})`); top.addColorStop(1,'rgba(0,0,0,0)');
        ctx.fillStyle=top; ctx.fillRect(sx,sy,sw,Math.min(depth,sh));
        const left=ctx.createLinearGradient(sx,0,sx+depth,0);
        left.addColorStop(0,`rgba(0,0,0,${alpha})`); left.addColorStop(1,'rgba(0,0,0,0)');
        ctx.fillStyle=left; ctx.fillRect(sx,sy,Math.min(depth,sw),sh);
      };
      ctx.drawImage(this.previewImage,px+border,py+border,g.photoW*scale,g.photoH*scale);
      if (matX>0 || matY>0) insetShadow(px,py,pw,ph,Math.min(4*scale,border || 2),0.18);
      if (frame>0) insetShadow(mx,my,mw,mh,Math.min(10*scale,Math.min(matX,matY)+border || 2),0.25);
    }
    submit() {
      this.clearResult();
      if (this.dataset.mode !== 'demo') return;
      if (this.pending) { this.error('filePending'); return; }
      const invalid = Array.from(this.form.elements).find(el => el.willValidate && !el.checkValidity());
      if (invalid) { this.error('invalid',invalid); return; }
      if (!this.fileInfo && !this.field('alternate_transfer').checked) { this.error('fileRequired',this.querySelector('[data-file]')); return; }
      const summary = this.querySelector('[data-summary]');
      summary.replaceChildren();
      const add = (label,value) => {
        const dt = document.createElement('dt'); dt.textContent = label;
        const dd = document.createElement('dd'); dd.textContent = value;
        summary.append(dt,dd);
      };
      for (const el of Array.from(this.form.elements)) {
        if (el.disabled || !el.name || ['rights','alternate_transfer'].includes(el.name)) continue;
        const label = this.querySelector(`label[for="${el.id}"]`)?.textContent.trim();
        if (!label || !el.value) continue;
        add(label,el.tagName === 'SELECT' ? el.selectedOptions[0].textContent : el.value);
      }
      add(this.messages.fileLabel,this.fileInfo?.name || this.messages.alternate);
      if (this.geometry) add('Vizualizacijos matmenys',this.querySelector('[data-preview-dimensions]').textContent);
      const result = this.querySelector('[data-result]');
      result.hidden = false;
      result.focus();
    }
  }
  customElements.define(TAG, PersonalPrint);
})();
